import {range} from 'lodash';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';

import {useDebounce} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {EventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelToken,
  CancelTokenSource,
} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {
  InteractiveListItem,
  PreselectHandler,
} from '../../../../../libs/shared/ui/src/components/interactiveList/interactiveList';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {DropdownResults, DropdownSource} from '../source/dropdownSource';

/*
 * Constants.
 */

const defaultGracePeriod = 600;
const defaultAutocompleteDebounceInMs = 250;

/*
 * Props.
 */

interface AutocompleteDropdownProps extends DropdownProps {
  /** Data source: search items based on query typed by user. */
  source: DropdownSource;
  /** The current search query. */
  query: string;
  /** Height of individual rows. */
  rowHeight?: number;
  /** Whether to automatically select the first row. */
  shouldSelectFirst?: boolean;
  /** How long should we keep displaying previous results to avoid flicker (default = 300). */
  gracePeriod?: number;
  debounceInMs?: number;
  /** Detect when an item is about to be selected. */
  onItemPreselect?: EventHandler;
  /** Renders if the status is loading or no results */
  onEmptyOrLoadingRender?: () => React.ReactElement | null;
}

/*
 * Component.
 */

export const AutocompleteDropdown: FC<AutocompleteDropdownProps> = (props) => {
  const {query, onItemPreselect, onEmptyOrLoadingRender} = props;
  const {status, results} = useDropdownResults(
    props.source,
    props.query,
    props.gracePeriod,
    props.debounceInMs,
  );

  // Logic to remember what item was selected while we load.
  const areResultsForDifferentQuery = !results || results.query !== query;
  const preselectedItemRef = useRef<InteractiveListItem>();
  const onDropdownItemPreSelect: PreselectHandler = useCallback(
    (e, item) => {
      if (!areResultsForDifferentQuery || !results) {
        return;
      }

      // Check if the parent can handle.
      if (onItemPreselect) {
        onItemPreselect(e);
      }

      // If the parent handled, nothing for us to do.
      if (e.defaultPrevented) {
        return;
      }

      // Prevent clicks from doing anything while we're loading.
      e.preventDefault();

      // If the index is 0, remember so that we can act when we're done.
      if (item.index !== 0) {
        return;
      }

      preselectedItemRef.current = item;
    },
    [areResultsForDifferentQuery, results, onItemPreselect],
  );

  useEffect(() => {
    // If there's no preselected item, nothing to do.
    const currentPreselectedItem = preselectedItemRef.current;
    if (!results || !currentPreselectedItem) {
      return;
    }

    // Otherwise, clear the preselected item and select the first one.
    preselectedItemRef.current = undefined;
    if (results.rowCount === 0) {
      return;
    }

    currentPreselectedItem.selectAtIndex(0);
  }, [results]);

  // No results, nothing to render.
  if (status !== AsyncStatusesEnum.LOADED || !results || results.rowCount === 0) {
    return onEmptyOrLoadingRender ? onEmptyOrLoadingRender() : null;
  }

  const itemRenders = range(results.rowCount).map((rowIndex) => results.renderRow(rowIndex, true));
  return (
    <SimpleDropdown
      {...props}
      onItemPreselect={onDropdownItemPreSelect}
      filterValue={results.query}
      maxWidth={props.maxWidth || 500}
      shouldSelectWithTab
      shouldSelectFirst
    >
      {itemRenders}
    </SimpleDropdown>
  );
};

interface AutocompleteDropdownState {
  status: AsyncStatusesEnum;
  results?: DropdownResults;
}

function useDropdownResults(
  source: DropdownSource,
  query: string,
  gracePeriod = defaultGracePeriod,
  debounceInMs = defaultAutocompleteDebounceInMs,
) {
  const [state, setState] = useState<AutocompleteDropdownState>({status: AsyncStatusesEnum.LOADED});

  // Cancellation.
  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  function maybeCancelRequest() {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  }

  // Grace period.
  const setStatusAfterGracePeriodAsync = useCallback(
    async (cancelToken: CancelToken) => {
      try {
        await delayAsync(gracePeriod, cancelToken);
        setState({status: AsyncStatusesEnum.LOADING});
      } catch {
        // Nothing to do.
      }
    },
    [gracePeriod],
  );

  // Request.
  const performSearchAsync = useCallback(async () => {
    // Cancel any existing request.
    maybeCancelRequest();

    // If the query is empty, nothing more to do.
    if (!query) {
      setState({status: AsyncStatusesEnum.LOADED});
      return;
    }

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;
    try {
      setStatusAfterGracePeriodAsync(cancelTokenSource.token);
      const results = await source.searchAsync(query, cancelTokenSource.token);
      setState({status: AsyncStatusesEnum.LOADED, results});
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        return;
      }

      // Otherwise, display a failed status.
      // eslint-disable-next-line no-console
      console.error(error);
      setState({status: AsyncStatusesEnum.FAILED});
    } finally {
      cancelTokenSource.cancel();
    }
  }, [query, setStatusAfterGracePeriodAsync, source]);

  const performSearchDebounced = useDebounce(performSearchAsync, debounceInMs);

  // Lifecycle.
  const lastQueryRef = useRef<string>();
  useEffect(() => {
    if (lastQueryRef.current === query) {
      return;
    }

    lastQueryRef.current = query;
    performSearchDebounced();
  }, [query, performSearchDebounced]);

  useInitialEffect(() => () => maybeCancelRequest());

  return state;
}
